import { z } from 'zod'
import { textoObrigatorio } from './comum'

/**
 * FaroEscolar Marketing (ADR 0057): o agendamento de posts nas redes sociais,
 * no desenho do mLabs. Uma marca junta os perfis de redes de um mesmo dono
 * (a FaroEscolar, uma escola); o post sai para vários perfis de uma vez, com
 * formato próprio em cada rede.
 *
 * As regras de cada rede (limite de legenda, quantas mídias, se exige vídeo)
 * moram aqui e em nenhum outro lugar: a tela avisa enquanto a pessoa escreve,
 * e o servidor recusa com a mesma conta. Escritas dos dois lados, um dia a tela
 * deixaria agendar o que a rede recusaria na hora de publicar.
 */

export const REDES_SOCIAIS = [
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'youtube',
  'pinterest',
  'google_empresa',
  'threads',
  'x',
  'bluesky',
] as const
export type RedeSocial = (typeof REDES_SOCIAIS)[number]

export const FORMATOS_DE_POST = ['post', 'reels', 'story', 'video', 'shorts', 'pin'] as const
export type FormatoDePost = (typeof FORMATOS_DE_POST)[number]

export interface DadosDaRede {
  nome: string
  /** Cor da marca da rede: é dado (identifica a rede), não cor de tela. */
  cor: string
  /** Formatos que a rede aceita, o primeiro é o padrão. */
  formatos: FormatoDePost[]
  limiteLegenda: number
  /** Só o Instagram conta hashtag: passou de 30, a publicação é recusada. */
  limiteHashtags: number | null
  /** Mídias num post comum (carrossel). Reels, vídeo, story e pin levam uma só. */
  limiteMidias: number
  /** Sem mídia a rede não publica (Instagram, TikTok, YouTube, Pinterest). */
  exigeMidia: boolean
  /** O Bluesky, por enquanto, só recebe foto daqui. */
  aceitaVideo: boolean
  /** Vídeo sai sozinho, sem foto junto (Facebook, LinkedIn, X). */
  videoSozinho: boolean
}

export const DADOS_DA_REDE: Record<RedeSocial, DadosDaRede> = {
  instagram: {
    nome: 'Instagram',
    cor: '#e1306c',
    formatos: ['post', 'reels', 'story'],
    limiteLegenda: 2200,
    limiteHashtags: 30,
    limiteMidias: 10,
    exigeMidia: true,
    aceitaVideo: true,
    videoSozinho: false,
  },
  facebook: {
    nome: 'Facebook',
    cor: '#1877f2',
    formatos: ['post', 'reels', 'story'],
    limiteLegenda: 63206,
    limiteHashtags: null,
    limiteMidias: 10,
    exigeMidia: false,
    aceitaVideo: true,
    videoSozinho: true,
  },
  linkedin: {
    nome: 'LinkedIn',
    cor: '#0a66c2',
    formatos: ['post'],
    limiteLegenda: 3000,
    limiteHashtags: null,
    limiteMidias: 9,
    exigeMidia: false,
    aceitaVideo: true,
    videoSozinho: true,
  },
  tiktok: {
    nome: 'TikTok',
    cor: '#111111',
    formatos: ['video'],
    limiteLegenda: 2200,
    limiteHashtags: null,
    limiteMidias: 1,
    exigeMidia: true,
    aceitaVideo: true,
    videoSozinho: false,
  },
  youtube: {
    nome: 'YouTube',
    cor: '#ff0000',
    formatos: ['video', 'shorts'],
    limiteLegenda: 5000,
    limiteHashtags: null,
    limiteMidias: 1,
    exigeMidia: true,
    aceitaVideo: true,
    videoSozinho: false,
  },
  pinterest: {
    nome: 'Pinterest',
    cor: '#e60023',
    formatos: ['pin'],
    limiteLegenda: 500,
    limiteHashtags: null,
    limiteMidias: 1,
    exigeMidia: true,
    aceitaVideo: true,
    videoSozinho: false,
  },
  google_empresa: {
    nome: 'Perfil da Empresa no Google',
    cor: '#34a853',
    formatos: ['post'],
    limiteLegenda: 1500,
    limiteHashtags: null,
    limiteMidias: 1,
    exigeMidia: false,
    aceitaVideo: false,
    videoSozinho: false,
  },
  threads: {
    nome: 'Threads',
    cor: '#000000',
    formatos: ['post'],
    limiteLegenda: 500,
    limiteHashtags: null,
    limiteMidias: 10,
    exigeMidia: false,
    aceitaVideo: true,
    videoSozinho: false,
  },
  x: {
    nome: 'X (Twitter)',
    cor: '#000000',
    formatos: ['post'],
    limiteLegenda: 280,
    limiteHashtags: null,
    limiteMidias: 4,
    exigeMidia: false,
    aceitaVideo: true,
    videoSozinho: true,
  },
  bluesky: {
    nome: 'Bluesky',
    cor: '#0085ff',
    formatos: ['post'],
    limiteLegenda: 300,
    limiteHashtags: null,
    limiteMidias: 4,
    exigeMidia: false,
    aceitaVideo: false,
    videoSozinho: false,
  },
}

export function rotuloDoFormato(rede: RedeSocial, formato: FormatoDePost): string {
  if (formato === 'post') return rede === 'instagram' ? 'Feed' : 'Post'
  return { reels: 'Reels', story: 'Story', video: 'Vídeo', shorts: 'Shorts', pin: 'Pin' }[formato]
}

/** Formatos de uma mídia só, e de vídeo obrigatório. */
const SO_UMA_MIDIA: FormatoDePost[] = ['reels', 'story', 'video', 'shorts', 'pin']
const SO_VIDEO: FormatoDePost[] = ['reels', 'video', 'shorts']

export const MIMES_DE_MIDIA_DE_POST = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'video/mp4',
  'video/quicktime',
] as const

export function midiaDePostAceita(mime: string | null | undefined): boolean {
  const limpo = (mime ?? '').split(';')[0]?.trim().toLowerCase() ?? ''
  return (MIMES_DE_MIDIA_DE_POST as readonly string[]).includes(limpo)
}

/**
 * A primeira linha da legenda, curta, para aviso, log e lista. Eram três
 * cópias (api, worker e tela), e a da api cortava em 58 caracteres.
 */
export function trechoDaLegenda(legenda: string, tamanho = 60): string {
  const linha = legenda.trim().split('\n')[0] ?? ''
  return linha.length > tamanho ? `${linha.slice(0, tamanho - 1)}…` : linha || 'Post sem legenda'
}

export function ehVideo(mime: string): boolean {
  return mime.toLowerCase().startsWith('video/')
}

export function contarHashtags(texto: string): number {
  return texto.match(/(^|\s)#[\p{L}\p{N}_]+/gu)?.length ?? 0
}

/**
 * O que impede o post de sair em cada perfil escolhido. Vazio é pronto.
 *
 * É a mesma conta na tela (aviso ao lado da prévia) e no servidor (recusa ao
 * agendar). Rascunho pode ter problema: é para isso que existe.
 */
export function problemasDoDestino(
  rede: RedeSocial,
  formato: FormatoDePost,
  legenda: string,
  midias: { mime: string }[],
): string[] {
  const dados = DADOS_DA_REDE[rede]
  const nome = `${dados.nome} (${rotuloDoFormato(rede, formato)})`
  const problemas: string[] = []

  if (!dados.formatos.includes(formato)) {
    problemas.push(`${dados.nome} não publica ${rotuloDoFormato(rede, formato)}.`)
    return problemas
  }
  if (legenda.length > dados.limiteLegenda) {
    problemas.push(
      `${nome}: a legenda tem ${legenda.length} caracteres, e o limite é ${dados.limiteLegenda}.`,
    )
  }
  if (dados.limiteHashtags !== null && contarHashtags(legenda) > dados.limiteHashtags) {
    problemas.push(`${nome}: no máximo ${dados.limiteHashtags} hashtags.`)
  }
  if (midias.length === 0) {
    if (dados.exigeMidia || formato !== 'post') problemas.push(`${nome}: precisa de foto ou vídeo.`)
    else if (!legenda.trim()) problemas.push(`${nome}: escreva a legenda ou ponha uma mídia.`)
    return problemas
  }
  const limite = SO_UMA_MIDIA.includes(formato) ? 1 : dados.limiteMidias
  if (midias.length > limite) {
    problemas.push(
      limite === 1
        ? `${nome}: leva uma mídia só.`
        : `${nome}: no máximo ${limite} mídias.`,
    )
  }
  if (SO_VIDEO.includes(formato) && !midias.every((m) => ehVideo(m.mime))) {
    problemas.push(`${nome}: precisa ser vídeo.`)
  }
  const videos = midias.filter((m) => ehVideo(m.mime)).length
  if (videos > 0 && !dados.aceitaVideo) {
    problemas.push(`${nome}: por enquanto sai só com foto.`)
  } else if (videos > 0 && dados.videoSozinho && midias.length > 1) {
    problemas.push(`${nome}: vídeo sai sozinho, sem outra foto ou vídeo junto.`)
  }
  return problemas
}

export const STATUS_DE_POST = [
  'rascunho',
  'em_aprovacao',
  'reprovado',
  'agendado',
  'na_hora',
  'publicado',
] as const
export type StatusDePost = (typeof STATUS_DE_POST)[number]

export const ROTULO_STATUS_DE_POST: Record<StatusDePost, string> = {
  rascunho: 'Rascunho',
  em_aprovacao: 'Em aprovação',
  reprovado: 'Reprovado',
  agendado: 'Agendado',
  na_hora: 'Hora de publicar',
  publicado: 'Publicado',
}

export const STATUS_DE_DESTINO = ['pendente', 'avisado', 'publicando', 'publicado', 'falhou'] as const
export type StatusDeDestino = (typeof STATUS_DE_DESTINO)[number]

/**
 * Quem faz o login de cada rede para publicar sozinho (ADR 0058). A Meta
 * conecta o Instagram e o Facebook de uma vez (a conta do Instagram ligada à
 * Página); o Threads tem login próprio; o Google é o do YouTube. O Bluesky não
 * tem app: conecta com o @ e uma senha de app criada nas configurações dele.
 * LinkedIn e Perfil da Empresa no Google seguem por lembrete.
 */
export const PROVEDORES_DE_CONEXAO = [
  'meta',
  'threads',
  'pinterest',
  'tiktok',
  'google',
  'x',
  'bluesky',
] as const
export type ProvedorDeConexao = (typeof PROVEDORES_DE_CONEXAO)[number]

export const DADOS_DO_PROVEDOR: Record<
  ProvedorDeConexao,
  { nome: string; redes: RedeSocial[]; jeito: 'login' | 'senha_de_app' }
> = {
  meta: { nome: 'Instagram e Facebook', redes: ['instagram', 'facebook'], jeito: 'login' },
  threads: { nome: 'Threads', redes: ['threads'], jeito: 'login' },
  pinterest: { nome: 'Pinterest', redes: ['pinterest'], jeito: 'login' },
  tiktok: { nome: 'TikTok', redes: ['tiktok'], jeito: 'login' },
  google: { nome: 'YouTube', redes: ['youtube'], jeito: 'login' },
  x: { nome: 'X (Twitter)', redes: ['x'], jeito: 'login' },
  bluesky: { nome: 'Bluesky', redes: ['bluesky'], jeito: 'senha_de_app' },
}

/**
 * Como o post chega na rede. `lembrete` é o do mLabs para perfil sem conexão:
 * na hora marcada a pessoa recebe o aviso com o texto e as mídias e publica
 * pelo celular. `automatico` fica para quando a conexão com a rede existir.
 */
export const MODOS_DE_PUBLICACAO = ['lembrete', 'automatico'] as const
export type ModoDePublicacao = (typeof MODOS_DE_PUBLICACAO)[number]

// ─────────────────────────────── entradas ───────────────────────────────

const corHex = z
  .string()
  .regex(/^#[0-9a-f]{6}$/i, 'cor inválida')

export const criarMarcaSchema = z.object({
  nome: textoObrigatorio('nome da marca', 80),
  cor: corHex.default('#2a6fdb'),
})
export type CriarMarcaEntrada = z.infer<typeof criarMarcaSchema>

export const atualizarMarcaSchema = z.object({
  nome: textoObrigatorio('nome da marca', 80).optional(),
  cor: corHex.optional(),
})
export type AtualizarMarcaEntrada = z.infer<typeof atualizarMarcaSchema>

const usuarioDaRede = z
  .string()
  .trim()
  .max(80, 'usuário passa de 80 caracteres')
  .transform((v) => v.replace(/^@+/, ''))
  .nullable()
  .optional()

export const criarPerfilSchema = z.object({
  marcaId: z.string().uuid('marca inválida'),
  rede: z.enum(REDES_SOCIAIS, { errorMap: () => ({ message: 'rede inválida' }) }),
  nome: textoObrigatorio('nome do perfil', 80),
  usuario: usuarioDaRede,
})
export type CriarPerfilEntrada = z.infer<typeof criarPerfilSchema>

export const atualizarPerfilSchema = z.object({
  nome: textoObrigatorio('nome do perfil', 80).optional(),
  usuario: usuarioDaRede,
  ativo: z.boolean().optional(),
  /** Pinterest: o quadro em que os pins entram. */
  quadro: z
    .object({ id: textoObrigatorio('quadro', 100), nome: textoObrigatorio('nome do quadro', 200) })
    .nullable()
    .optional(),
})
export type AtualizarPerfilEntrada = z.infer<typeof atualizarPerfilSchema>

export const salvarPostSchema = z.object({
  marcaId: z.string().uuid('marca inválida'),
  legenda: z.string().max(63206, 'legenda longa demais').default(''),
  primeiroComentario: z
    .string()
    .trim()
    .max(2200, 'primeiro comentário passa de 2200 caracteres')
    .nullable()
    .optional(),
  agendadoPara: z.string().datetime({ offset: true, message: 'data inválida' }).nullable(),
  destinos: z
    .array(
      z.object({
        perfilId: z.string().uuid('perfil inválido'),
        formato: z.enum(FORMATOS_DE_POST, { errorMap: () => ({ message: 'formato inválido' }) }),
      }),
    )
    .max(30, 'perfis demais num post só'),
  midiaIds: z.array(z.string().uuid('mídia inválida')).max(10, 'no máximo 10 mídias'),
  /** `agendar` vira "em aprovação" para quem não tem `marketing.aprovar`. */
  acao: z.enum(['rascunho', 'agendar']),
})
export type SalvarPostEntrada = z.infer<typeof salvarPostSchema>

export const conectarBlueskySchema = z.object({
  marcaId: z.string().uuid('marca inválida'),
  usuario: textoObrigatorio('usuário do Bluesky', 253).transform((v) => v.replace(/^@+/, '')),
  senhaDeApp: textoObrigatorio('senha de app', 64),
})
export type ConectarBlueskyEntrada = z.infer<typeof conectarBlueskySchema>

export const reprovarPostSchema = z.object({
  motivo: textoObrigatorio('motivo', 500),
})

export const filtroDePostsSchema = z.object({
  de: z.string().datetime({ offset: true }).optional(),
  ate: z.string().datetime({ offset: true }).optional(),
  marcaId: z.string().uuid().optional(),
  status: z.enum(STATUS_DE_POST).optional(),
})
export type FiltroDePosts = z.infer<typeof filtroDePostsSchema>

// ─────────────────────────────── saídas ───────────────────────────────

export interface PerfilSocialDto {
  id: string
  marcaId: string
  rede: RedeSocial
  nome: string
  usuario: string | null
  modo: ModoDePublicacao
  ativo: boolean
  /** A conexão que publica por este perfil; `null` no modo lembrete. */
  conexaoId: string | null
  quadro: { id: string; nome: string } | null
}

export interface ConexaoDeRedeDto {
  id: string
  marcaId: string
  provedor: ProvedorDeConexao
  nome: string
  /** Login vencido ou revogado: precisa conectar de novo. */
  problema: string | null
  criadoEm: string
}

export interface SituacaoDasConexoesDto {
  provedores: {
    id: ProvedorDeConexao
    /** O app da rede está no .env (ou não precisa de app, como o Bluesky). */
    configurado: boolean
    /** O endereço de retorno que vai no cadastro do app da rede. */
    urlDeRetorno: string | null
  }[]
  /** Por onde as redes buscam as mídias (o link público); `null` se não há. */
  urlPublica: string | null
  conexoes: ConexaoDeRedeDto[]
}

export interface MarcaDto {
  id: string
  nome: string
  cor: string
  perfis: PerfilSocialDto[]
}

export interface MidiaDePostDto {
  id: string
  mime: string
  nomeArquivo: string
  tamanho: number
  url: string
}

export interface DestinoDePostDto {
  id: string
  perfilId: string
  rede: RedeSocial
  perfilNome: string
  perfilUsuario: string | null
  formato: FormatoDePost
  status: StatusDeDestino
  publicadoEm: string | null
  /** Publica sozinho (perfil conectado) ou por lembrete. */
  automatico: boolean
  /** O que a rede respondeu quando não publicou. */
  erro: string | null
  /** O post na rede, quando ela devolve o endereço. */
  url: string | null
}

export interface PostDto {
  id: string
  marcaId: string
  marcaNome: string
  marcaCor: string
  legenda: string
  primeiroComentario: string | null
  status: StatusDePost
  agendadoPara: string | null
  publicadoEm: string | null
  criadoPorNome: string | null
  aprovadoPorNome: string | null
  aprovadoEm: string | null
  motivoReprovacao: string | null
  criadoEm: string
  atualizadoEm: string
  midias: MidiaDePostDto[]
  destinos: DestinoDePostDto[]
}
